import API from 'services/api';
import {
  assetGridActions,
  assetFilterActions,
  assetSelectors,
  userAssigneeListActions,
  assetPageSpecificActions,
  assetNS,
} from 'containers/Pages/Assets/Assets';
import isEmpty from 'lodash/isEmpty';
import { call, select, takeLatest, put, all } from 'redux-saga/effects';
import get from 'lodash/get';
import { setLoading, showSnackbar, toggleModal } from 'reducers/ui';
import { errorHandler } from 'utils/errorMessages';
import { getSecurityId } from 'reducers/users';
import { getSecurityCompanyRoles } from 'reducers/securityCompany';
import { ROLES } from 'utils/constants';
import { t } from 'i18next';
import { mediaUploadImages } from 'sagas/sitesSaga/sitesSaga';
import { PAGE_URLS } from 'utils/constants/index.js';
import { fetchPersonnelDetailsSaga } from 'sagas/personnelSaga/personnelSaga';

export function* fetchAssetListSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { filters, from } = payload;
    const securityCompanyId = yield select(getSecurityId);
    const departmentId = get(filters, 'departmentId');

    const options = {
      securityCompanyId,
      archived: get(filters, 'archived', false),
      category: get(filters, 'category'),
      type: get(filters, 'type'),
      search: get(filters, 'search'),
      status: get(filters, 'status'),
      from,
      departmentId,
    };
    const filterKeyStatus = yield select(assetSelectors.getAssetFilterKey);
    const forceRefresh = yield select(assetSelectors.getForceRefresh);
    const getAssetList = yield select(assetSelectors.getAssetListRaw);
    // if firebase sends a notification or cleanerList is empty or filter key is set, only then get the new list of assets
    if (isEmpty(getAssetList) || forceRefresh || filterKeyStatus) {
      const { data } = yield call(API.getAssetList, options);

      if (data.data !== null) {
        const totalEntries = get(data, 'meta.total');
        yield put(
          assetGridActions.setGridList({
            list: data.data,
            totalEntries,
          }),
        );
      }
    }
    if (filterKeyStatus) {
      yield put(assetFilterActions.setFilterKey(false));
    }
    if (forceRefresh) {
      yield put(assetGridActions.setForceRefresh(false));
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* fetchAssetDetailsSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { data } = yield call(API.fetchAssetDetail, payload);
    if (data.data) {
      yield put(assetGridActions.setGridRowDetails({ details: data.data }));
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* fetchAssetAssignees({ filters }) {
  try {
    const securityCompanyId = yield select(getSecurityId);
    const availableRoles = yield select(getSecurityCompanyRoles);

    const payload = {
      securityCompanyId,
      roles: [...availableRoles, ROLES.SECURITY_COMPANY_MANAGER.id],
      archived: get(filters, 'archived', false),
      status: get(filters, 'status', undefined),
    };

    const { data } = yield call(API.fetchUserList, payload);

    if (data.data !== null) {
      const totalEntries = get(data, 'meta.total');
      yield put(
        userAssigneeListActions.setUserList({ list: data.data, totalEntries }),
      );
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* assetHandoverSaga({ payload }) {
  const { id, giver, receiver, state, location } = payload;
  const { firstName, lastName } = receiver.general;
  try {
    yield put(setLoading(true));
    const { data } = yield call(API.assetHandover, {
      id,
      giver,
      receiver,
      state,
    });
    const userId = get(receiver, 'general.id');
    if (data.data) {
      yield call(fetchAssetDetailsSaga, { payload: { id } });
    }
    // if this saga is called from the personnel details, then refresh it
    if (location === PAGE_URLS.PERSONNEL.link) {
      yield call(fetchPersonnelDetailsSaga, {
        payload: { id: userId },
      });
    }

    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'success',
        message: `${t('Asset assigned to')} ${firstName} ${lastName}`,
      }),
    );
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* makeAssetAvailableSaga({ payload }) {
  try {
    const { id } = payload;
    const { status } = yield call(API.makeStatusAvailable, id);
    if (status === 200) {
      yield call(fetchAssetDetailsSaga, { payload: { id } });
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* createAssetSaga({ payload }) {
  try {
    const { asset } = payload;
    yield put(setLoading(true));

    if (asset.surfaces.length > 0) {
      const { surfaces } = asset;
      const imagesPayload = yield all(
        surfaces.map(surface => call(mediaUploadImages, surface)),
      );
      asset['surfaces'] = imagesPayload;
    }
    const { data } = yield call(API.addAsset, asset);

    if (data.data) {
      yield put(toggleModal(assetNS));
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* updateAssetSaga({ payload }) {
  try {
    const { id, asset } = payload;
    yield put(setLoading(true));

    if (asset.surfaces.length > 0) {
      const { surfaces } = asset;
      const imagesPayload = yield all(
        surfaces.map(surface => call(mediaUploadImages, surface)),
      );
      asset['images'] = imagesPayload;
    }
    const { status } = yield call(API.updateAsset, id, asset);

    if (status === 200) {
      yield call(fetchAssetDetailsSaga, { payload: { id } });
      yield put(toggleModal(assetNS));
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* makeAssetUnavailableSaga({ payload }) {
  try {
    const { id } = payload;
    const { status } = yield call(API.makeStatusUnavailable, id);
    if (status === 200) {
      yield call(fetchAssetDetailsSaga, { payload: { id } });
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* requestAssetReturnSaga({ payload }) {
  const { assetAssigneeId, id, assetId, name } = payload;
  yield put(setLoading(true));
  try {
    const data = yield call(API.requestAssetReturn, {
      assetAssigneeId,
      id,
      assetId,
      name,
    });

    if (data.status === 200) {
      yield put(
        showSnackbar({
          typeOfSnackbar: 'success',
          message: 'Message sent',
        }),
      );
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* assetsSagas() {
  yield takeLatest(assetGridActions.fetchGridList, fetchAssetListSaga);
  yield takeLatest(assetGridActions.fetchGridRowDetails, fetchAssetDetailsSaga);
  yield takeLatest(userAssigneeListActions.fetchUserList, fetchAssetAssignees);
  yield takeLatest(assetPageSpecificActions.assetHandover, assetHandoverSaga);
  yield takeLatest(assetGridActions.createRowEntry, createAssetSaga);
  yield takeLatest(assetGridActions.updateRowEntry, updateAssetSaga);
  yield takeLatest(
    assetPageSpecificActions.makeAssetAvailable,
    makeAssetAvailableSaga,
  );
  yield takeLatest(
    assetPageSpecificActions.makeAssetUnavailable,
    makeAssetUnavailableSaga,
  );
  yield takeLatest(
    assetPageSpecificActions.requestAssetReturn,
    requestAssetReturnSaga,
  );
}
